import { useTranslation } from 'react-i18next';
import React from 'react';
import clsx from 'clsx';
import {
  P2GBox,
  P2GChip,
  P2GGrid,
  P2GIcon,
  P2GStepper,
  P2GText,
  P2GTooltip,
  SvgConfirmCircle,
  useP2GMediaQueryDown
} from '@parcel2go/core-ui';
import { Service, StageOverview } from '~/lib/types/tracking-types';
import { translatedTrackingStage } from '../utils/mapParcelStage';
import { TranslationNamespaces } from '~/lib/constants';

interface IStageOverviewDescriptionProps {
  completed: number;
  in: number;
  parcels: number;
}

export const StageOverviewDescription: React.FC<IStageOverviewDescriptionProps> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.TRACKING);
  const all = props.completed >= props.parcels && props.in === 0;
  return (
    <P2GTooltip
      title={t('RESULT_OVERVIEW_DESCRIPTION', {
        defaultValue: 'Overview of all of your parcels in this order'
      })}
    >
      <React.Fragment>
        {all && (
          <P2GIcon palette="primary" size="md">
            <SvgConfirmCircle />
          </P2GIcon>
        )}
        {!all && props.in > 0 && <P2GChip label={`${props.in}`}></P2GChip>}
      </React.Fragment>
    </P2GTooltip>
  );
};

interface IStageOverviewProps {
  overview: StageOverview;
  parcels: number;
  service: Service;
}

const StagesSmallOverview: React.FC<IStageOverviewProps> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.TRACKING);
  const { overview, parcels, service } = props;
  const steps = React.useMemo(
    () => [
      'TRACKING_STAGE_DROPPED_OFF',
      'TRACKING_STAGE_COLLECTED',
      'TRACKING_STAGE_IN_TRANSIT',
      'TRACKING_STAGE_AT_DEPOT',
      'TRACKING_STAGE_DELIVERY_SCHEDULED',
      'TRACKING_STAGE_DELIVERED'
    ],
    []
  );

  if (service && !service.isDropOff) {
    steps.shift();
  }

  const stage = React.useMemo(() => {
    if (overview) {
      if (overview.delivered && overview.delivered.in > 0) return 'TRACKING_STAGE_DELIVERED';
      if (overview.deliveryScheduled && overview.deliveryScheduled.in > 0) return 'TRACKING_STAGE_DELIVERY_SCHEDULED';
      if (overview.atDepot && overview.atDepot.in > 0) return 'TRACKING_STAGE_AT_DEPOT';
      if (overview.inTransit && overview.inTransit.in > 0) return 'TRACKING_STAGE_IN_TRANSIT';
      if (overview.collected && overview.collected.in > 0) return 'TRACKING_STAGE_COLLECTED';
      if (overview.droppedOff && overview.droppedOff.in > 0) return 'TRACKING_STAGE_DROPPED_OFF';
    }
    return steps[0];
  }, [overview, steps]);

  const page = React.useMemo(() => {
    if (overview && overview.delivered) {
      return overview.delivered.in >= parcels ? steps.length + 1 : steps.indexOf(stage);
    }
    return 0;
  }, [stage, parcels, overview, steps]);

  return (
    <>
      {parcels <= 1 && (
        <P2GBox pb={2}>
          <P2GGrid container spacing={2}>
            <P2GGrid item flexGrow={12}>
              <P2GStepper alternativeLabel={true} activeStep={page} steps={Array(steps.length).fill('')} size="large" />
            </P2GGrid>

            <P2GGrid container direction="column" justify="center" alignItems="center" spacing={1}>
              <P2GGrid item>
                <P2GText variant="h5" classNames={clsx('p2g-tracking-stage-title')}>
                  {translatedTrackingStage(t, stage)}
                </P2GText>
              </P2GGrid>
            </P2GGrid>
          </P2GGrid>
        </P2GBox>
      )}
    </>
  );
};

const StagesLargeOverview: React.FC<IStageOverviewProps> = (props) => {
  const { overview, parcels, service } = props;
  const { t } = useTranslation(TranslationNamespaces.TRACKING);

  const steps = React.useMemo(
    () => [
      'TRACKING_STAGE_DROPPED_OFF',
      'TRACKING_STAGE_COLLECTED',
      'TRACKING_STAGE_IN_TRANSIT',
      'TRACKING_STAGE_AT_DEPOT',
      'TRACKING_STAGE_DELIVERY_SCHEDULED',
      'TRACKING_STAGE_DELIVERED'
    ],
    []
  );

  if (service && !service.isDropOff) {
    steps.shift();
  }

  const stage = React.useMemo(() => {
    if (overview) {
      if (overview.delivered && overview.delivered.in > 0) return 'TRACKING_STAGE_DELIVERED';
      if (overview.deliveryScheduled && overview.deliveryScheduled.in > 0) return 'TRACKING_STAGE_DELIVERY_SCHEDULED';
      if (overview.atDepot && overview.atDepot.in > 0) return 'TRACKING_STAGE_AT_DEPOT';
      if (overview.inTransit && overview.inTransit.in > 0) return 'TRACKING_STAGE_IN_TRANSIT';
      if (overview.collected && overview.collected.in > 0) return 'TRACKING_STAGE_COLLECTED';
      if (overview.droppedOff && overview.droppedOff.in > 0) return 'TRACKING_STAGE_DROPPED_OFF';
    }
    return steps[0];
  }, [overview, steps]);

  const page = React.useMemo(() => {
    if (overview && overview.delivered) {
      return overview.delivered.in >= parcels ? steps.length + 1 : steps.indexOf(stage);
    }
    return 0;
  }, [stage, parcels, overview, steps]);

  return (
    <>
      {parcels <= 1 && (
        <P2GStepper
          orientation="vertical"
          activeStep={page}
          steps={steps.map((step) => translatedTrackingStage(t, step))}
          size="large"
        />
      )}
    </>
  );
};

export const StagesOverview: React.FC<IStageOverviewProps> = (props) => {
  const isBreak = useP2GMediaQueryDown('sm');
  if (isBreak) {
    return <StagesSmallOverview {...props} />;
  }
  return <StagesLargeOverview {...props} />;
};
